import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Avatar, Button, Card, CardContent, TextField,
  Grid, Chip, Divider, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, IconButton,
  Tooltip, List, ListItem, ListItemIcon, ListItemText, Dialog,
  DialogTitle, DialogContent, DialogActions, LinearProgress, Slider,
  Tab, Tabs,
} from '@mui/material';
import {
  Edit, Save, Cancel, AccountCircle, Tune, BarChart, ExpandMore,
  Notifications, Satellite, Dashboard, EmojiEvents, Settings,
  Security, PhotoCamera, Lock, Logout, CheckCircle,
  RadioButtonUnchecked, Star, RocketLaunch, Explore, NightsStay,
  Login, Language, VolumeUp, Refresh, Straighten, MyLocation,
  Email, Phone, LocationOn, Public, Schedule, AccessTime,
  TrendingUp, Shield,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { profileAPI } from '../services/api';
import { format, parseISO } from 'date-fns';

// ─── Sidebar sections definition ──────────────────────────────────────────────
const SECTIONS = [
  { id: 'basic',       label: 'Profile',          icon: <AccountCircle /> },
  { id: 'preferences', label: 'Space Preferences', icon: <Tune /> },
  { id: 'activity',    label: 'Activity Insights', icon: <BarChart /> },
  { id: 'alerts',      label: 'Alert Management',  icon: <Notifications />, authRequired: true },
  { id: 'watchlist',   label: 'Satellite Watchlist',icon: <Satellite />,    authRequired: true },
  { id: 'personal',    label: 'Personal Dashboard', icon: <Dashboard />,    authRequired: true },
  { id: 'badges',      label: 'Achievements',       icon: <EmojiEvents /> },
  { id: 'settings',    label: 'App Settings',        icon: <Settings /> },
  { id: 'security',    label: 'Security',            icon: <Security />,    authRequired: true },
];

const fmtDate = (dt) => {
  if (!dt) return '—';
  try { return format(typeof dt === 'string' ? parseISO(dt) : new Date(dt), 'MMM d, yyyy'); }
  catch { return '—'; }
};
const fmtDateTime = (dt) => {
  if (!dt) return '—';
  try { return format(typeof dt === 'string' ? parseISO(dt) : new Date(dt), 'MMM d, yyyy HH:mm'); }
  catch { return '—'; }
};

// ─── Achievement badge component ──────────────────────────────────────────────
function BadgeCard({ badge }) {
  return (
    <Card sx={{
      bgcolor: badge.earned ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${badge.earned ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 2,
      p: 2,
      textAlign: 'center',
      opacity: badge.earned ? 1 : 0.5,
      transition: 'all 0.2s',
      '&:hover': { opacity: 1, borderColor: 'rgba(255,255,255,0.3)' },
    }}>
      <Typography sx={{ fontSize: 36, mb: 0.5 }}>{badge.icon}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: badge.earned ? 'primary.light' : 'text.secondary', fontSize: 13 }}>
        {badge.title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        {badge.description}
      </Typography>
      {badge.earned ? (
        <Chip label="Earned" size="small" sx={{ mt: 1, bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', fontSize: 10 }} />
      ) : (
        <Chip label="Locked" size="small" sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.05)', color: 'text.secondary', fontSize: 10 }} />
      )}
    </Card>
  );
}

// ─── Section title component ──────────────────────────────────────────────────
function SectionTitle({ icon, title, subtitle }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4.5 }}>{subtitle}</Typography>
      )}
      <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, setUser, logout, isGuest, openAuthModal, requireAuth } = useAuth();
  const navigate    = useNavigate();
  const [params]    = useSearchParams();

  const tabFromUrl  = params.get('tab');
  const [section, setSection] = useState(tabFromUrl || 'basic');
  const [mobileTabOpen, setMobileTabOpen] = useState(false);

  // Profile data state
  const [profile, setProfile]   = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [form, setForm]         = useState({});

  // Change password dialog
  const [pwDialog, setPwDialog]   = useState(false);
  const [pwForm, setPwForm]       = useState({ currentPassword: '', newPassword: '', confirmNew: '' });
  const [pwError, setPwError]     = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // ── Load profile data ────────────────────────────────────────────────────
  useEffect(() => {
    if (isGuest) {
      setLoading(false);
      return;
    }
    Promise.all([profileAPI.getProfile(), profileAPI.getActivity()])
      .then(([pRes, aRes]) => {
        setProfile(pRes.data.data);
        setActivity(aRes.data.data);
        setForm(pRes.data.data);
      })
      .catch(() => setError('Failed to load profile data'))
      .finally(() => setLoading(false));
  }, [isGuest]);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await profileAPI.updateProfile({
        name: form.name, phone: form.phone, bio: form.bio,
        location: form.location, timezone: form.timezone,
        defaultDashboard: form.defaultDashboard,
        units: form.units, coordinateFormat: form.coordinateFormat,
        language: form.language, soundNotifications: form.soundNotifications,
        emailNotifications: form.emailNotifications,
        autoRefreshInterval: form.autoRefreshInterval,
        theme: form.theme,
      });
      setProfile(res.data.data);
      setUser(prev => ({ ...prev, ...res.data.data }));
      localStorage.setItem('user', JSON.stringify({ ...user, ...res.data.data }));
      setSuccess('Profile updated successfully!');
      setEditMode(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirmNew) { setPwError('Passwords do not match'); return; }
    setPwError(''); setPwLoading(true);
    try {
      await profileAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwDialog(false);
      setPwForm({ currentPassword: '', newPassword: '', confirmNew: '' });
      setSuccess('Password changed successfully!');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };


  const f = (key) => form[key] ?? profile?.[key] ?? '';
  const fBool = (key) => form[key] ?? profile?.[key] ?? false;

  // ── Guest wall ───────────────────────────────────────────────────────────
  if (isGuest) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Card sx={{
          maxWidth: 400, width: '100%', textAlign: 'center', p: 4,
          bgcolor: 'rgba(10,22,40,0.8)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3,
        }}>
          <Typography sx={{ fontSize: 56, mb: 2 }}>👤</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.light', mb: 1 }}>
            Your Profile Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Sign in to access your full profile, achievements, alert history, satellite watchlist, and personalized space dashboard.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" startIcon={<Login />} onClick={() => openAuthModal(0)} sx={{ px: 3 }}>
              Sign In
            </Button>
            <Button variant="outlined" onClick={() => openAuthModal(1)}
              sx={{ px: 3, borderColor: 'rgba(255,255,255,0.4)', color: 'primary.light' }}>
              Create Account
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography sx={{ color: 'text.secondary' }}>Loading your profile...</Typography>
      </Box>
    );
  }

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const Sidebar = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* Profile header in sidebar */}
      <Box sx={{ p: 2.5, textAlign: 'center', mb: 1 }}>
        <Avatar
          src={profile?.profilePhoto}
          sx={{ width: 70, height: 70, mx: 'auto', mb: 1.5, bgcolor: 'primary.dark', fontSize: 28, fontWeight: 700 }}
        >
          {profile?.name?.[0]?.toUpperCase()}
        </Avatar>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
          {profile?.name}
        </Typography>
        <Chip
          label={profile?.accountType === 'PRO' ? '⭐ Pro' : 'Free'}
          size="small"
          sx={{
            mt: 0.5,
            bgcolor: profile?.accountType === 'PRO' ? 'rgba(255,167,38,0.15)' : 'rgba(255,255,255,0.06)',
            color: profile?.accountType === 'PRO' ? '#ffa726' : 'text.secondary',
            fontSize: 11,
          }}
        />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 0.5 }} />

      {SECTIONS.map((s) => (
        <Box
          key={s.id}
          onClick={() => setSection(s.id)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2, py: 1.2, borderRadius: 2, cursor: 'pointer',
            bgcolor: section === s.id ? 'rgba(255,255,255,0.12)' : 'transparent',
            borderLeft: section === s.id ? '3px solid' : '3px solid transparent',
            borderLeftColor: section === s.id ? 'primary.main' : 'transparent',
            color: section === s.id ? 'primary.light' : 'text.secondary',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            transition: 'all 0.15s',
          }}
        >
          <Box sx={{ fontSize: 18, lineHeight: 1, color: 'inherit' }}>{s.icon}</Box>
          <Typography variant="body2" sx={{ fontWeight: section === s.id ? 700 : 400, color: 'inherit', fontSize: 13 }}>
            {s.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // ── Section: Basic Info ──────────────────────────────────────────────────
  const BasicInfoSection = () => (
    <Box>
      <SectionTitle icon={<AccountCircle />} title="Basic Information" subtitle="Your profile and account details" />

      {/* Avatar + upload */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 3 }, mb: { xs: 3, sm: 4 }, flexWrap: 'wrap' }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={profile?.profilePhoto}
            sx={{ width: { xs: 64, sm: 90 }, height: { xs: 64, sm: 90 }, bgcolor: 'primary.dark', fontSize: { xs: 26, sm: 36 }, fontWeight: 700 }}
          >
            {profile?.name?.[0]?.toUpperCase()}
          </Avatar>
          {editMode && (
            <Tooltip title="Upload photo (paste image URL in Photo URL field below)">
              <IconButton
                size="small"
                sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' },
                  width: 28, height: 28,
                }}
              >
                <PhotoCamera sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: 16, sm: 20 } }}>
            {profile?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: { xs: 12, sm: 14 } }}>
            {profile?.email}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              label={profile?.accountType === 'PRO' ? '⭐ Pro Member' : '🆓 Free Tier'}
              size="small"
              sx={{
                bgcolor: profile?.accountType === 'PRO' ? 'rgba(255,167,38,0.15)' : 'rgba(102,187,106,0.1)',
                color: profile?.accountType === 'PRO' ? '#ffa726' : '#66bb6a', fontSize: 11,
              }}
            />
            <Chip
              label={`Member since ${fmtDate(profile?.createdAt)}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', fontSize: 11 }}
            />
          </Box>
        </Box>

        <Box sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
          {editMode ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} /> : <Save />}
                onClick={handleSave} disabled={saving} sx={{ borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>
                Save
              </Button>
              <Button variant="outlined" size="small" startIcon={<Cancel />}
                onClick={() => { setEditMode(false); setForm(profile); }} disabled={saving}
                sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.2)', flex: { xs: 1, sm: 'none' } }}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => setEditMode(true)}
              sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light', width: { xs: '100%', sm: 'auto' } }}>
              Edit Profile
            </Button>
          )}
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Full Name" value={f('name')}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            disabled={!editMode} size="small"
            InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'primary.main', display: 'flex' }}><AccountCircle fontSize="small" /></Box> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Email Address" value={f('email')} disabled size="small"
            helperText="Email cannot be changed"
            InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'primary.main', display: 'flex' }}><Email fontSize="small" /></Box> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Phone Number" value={f('phone')}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            disabled={!editMode} size="small"
            InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'primary.main', display: 'flex' }}><Phone fontSize="small" /></Box> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Location" value={f('location')}
            onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
            disabled={!editMode} size="small"
            placeholder="City, Country (for ISS / Aurora predictions)"
            InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'primary.main', display: 'flex' }}><LocationOn fontSize="small" /></Box> }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Bio" value={f('bio')}
            onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
            disabled={!editMode} size="small" multiline rows={2}
            placeholder="Tell the SpacePulse community about yourself..."
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
        {editMode && (
          <Grid item xs={12}>
            <TextField fullWidth label="Profile Photo URL" value={f('profilePhoto')}
              onChange={e => setForm(p => ({ ...p, profilePhoto: e.target.value }))}
              size="small" placeholder="https://example.com/your-photo.jpg"
              InputProps={{ startAdornment: <Box sx={{ mr: 1, color: 'primary.main', display: 'flex' }}><PhotoCamera fontSize="small" /></Box> }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // ── Section: Space Preferences ───────────────────────────────────────────
  const SpacePreferencesSection = () => (
    <Box>
      <SectionTitle icon={<Tune />} title="Space Preferences" subtitle="Customize your space monitoring experience" />
      {!editMode && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => setEditMode(true)}
            sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light', width: { xs: '100%', sm: 'auto' } }}>
            Edit Preferences
          </Button>
        </Box>
      )}
      {editMode && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} /> : <Save />}
            onClick={handleSave} disabled={saving} sx={{ borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>Save</Button>
          <Button variant="outlined" size="small" startIcon={<Cancel />}
            onClick={() => { setEditMode(false); setForm(profile); }}
            sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.2)', flex: { xs: 1, sm: 'none' } }}>Cancel</Button>
        </Box>
      )}

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={!editMode}>
            <InputLabel>Timezone</InputLabel>
            <Select value={f('timezone')} label="Timezone"
              onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}>
              {['UTC','America/New_York','America/Chicago','America/Los_Angeles','America/Denver',
                'Europe/London','Europe/Paris','Europe/Berlin','Asia/Tokyo','Asia/Kolkata',
                'Asia/Shanghai','Australia/Sydney','Pacific/Auckland'].map(tz => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={!editMode}>
            <InputLabel>Default Dashboard View</InputLabel>
            <Select value={f('defaultDashboard')} label="Default Dashboard View"
              onChange={e => setForm(p => ({ ...p, defaultDashboard: e.target.value }))}>
              {[
                { value: 'home',       label: 'Home' },
                { value: 'dashboard',  label: 'Space Weather' },
                { value: 'events',     label: 'Space Events' },
                { value: 'iss-tracker',label: 'ISS Tracker' },
                { value: 'moon',       label: 'Moon Dashboard' },
                { value: 'night-sky',  label: 'Night Sky' },
              ].map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={!editMode}>
            <InputLabel>Distance Units</InputLabel>
            <Select value={f('units') || 'KM'} label="Distance Units"
              onChange={e => setForm(p => ({ ...p, units: e.target.value }))}>
              <MenuItem value="KM">Kilometers (km)</MenuItem>
              <MenuItem value="MILES">Miles (mi)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={!editMode}>
            <InputLabel>Coordinate Format</InputLabel>
            <Select value={f('coordinateFormat') || 'DECIMAL'} label="Coordinate Format"
              onChange={e => setForm(p => ({ ...p, coordinateFormat: e.target.value }))}>
              <MenuItem value="DECIMAL">Decimal Degrees (40.7128°)</MenuItem>
              <MenuItem value="DMS">DMS (40° 42' 46" N)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Section: Activity Insights ────────────────────────────────────────────
  const ActivitySection = () => {
    const earnedCount = activity?.achievements?.filter(a => a.earned).length || 0;
    const totalBadges = activity?.achievements?.length || 6;

    return (
      <Box>
        <SectionTitle icon={<BarChart />} title="Activity Insights" subtitle="Your SpacePulse usage statistics" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Logins', value: activity?.loginCount || 0, icon: <Login />, color: '#4fc3f7' },
            { label: 'Last Login',   value: fmtDate(activity?.lastLoginAt), icon: <AccessTime />, color: '#66bb6a' },
            { label: 'Member Since', value: fmtDate(activity?.memberSince), icon: <Star />, color: '#ffa726' },
            { label: 'Badges Earned', value: `${earnedCount} / ${totalBadges}`, icon: <EmojiEvents />, color: '#f48fb1' },
          ].map((stat) => (
            <Grid item xs={6} sm={3} key={stat.label}>
              <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 2, textAlign: 'center' }}>
                <Box sx={{ color: stat.color, mb: 0.5 }}>{stat.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: 18 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stat.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Achievement progress */}
        <Card sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Achievement Progress
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
              {earnedCount}/{totalBadges} earned
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={totalBadges > 0 ? (earnedCount / totalBadges) * 100 : 0}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 4 },
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            {totalBadges - earnedCount} badges remaining — keep exploring!
          </Typography>
        </Card>
      </Box>
    );
  };

  // ── Section: Alert Management ─────────────────────────────────────────────
  const AlertManagementSection = () => (
    <Box>
      <SectionTitle icon={<Notifications />} title="Alert Management" subtitle="Configure and manage your space weather alerts" />
      <Card sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, p: 3, textAlign: 'center' }}>
        <Notifications sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
          Manage Your Alerts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          Create and manage alerts for solar flares, geomagnetic storms, CMEs, and more.
          Get notified instantly when space weather events occur.
        </Typography>
        <Button variant="contained" startIcon={<Notifications />} onClick={() => navigate('/alerts')}
          sx={{ borderRadius: 2 }}>
          Go to Alert Center
        </Button>
      </Card>
    </Box>
  );

  // ── Section: Satellite Watchlist ──────────────────────────────────────────
  const WatchlistSection = () => {
    const sats = profile?.favoriteSatellites ? JSON.parse(profile.favoriteSatellites) : [];
    return (
      <Box>
        <SectionTitle icon={<Satellite />} title="Satellite Watchlist" subtitle="Your favorite satellites for quick access" />
        {sats.length === 0 ? (
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 3, textAlign: 'center' }}>
            <Satellite sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              No satellites in your watchlist yet.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/satellite-tracker')}
              sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light' }}>
              Browse Satellites
            </Button>
          </Card>
        ) : (
          <Grid container spacing={1.5}>
            {sats.map((sat, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Satellite sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{sat}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  // ── Section: Personal Dashboard ───────────────────────────────────────────
  const PersonalDashboardSection = () => (
    <Box>
      <SectionTitle icon={<Dashboard />} title="Personal Dashboard" subtitle="Your pinned pages and recently viewed content" />
      <Grid container spacing={2}>
        {[
          { title: 'Space Weather', path: '/dashboard', icon: '☀️', desc: 'Solar activity monitor' },
          { title: 'ISS Tracker',   path: '/iss-tracker', icon: '🛸', desc: 'Live ISS position' },
          { title: 'Moon Dashboard', path: '/moon', icon: '🌕', desc: 'Lunar phase & rise/set' },
          { title: 'Night Sky',     path: '/night-sky', icon: '🌌', desc: 'Star map & constellations' },
          { title: 'Space Events',  path: '/events', icon: '⚡', desc: 'Solar flares & CMEs' },
          { title: 'Exoplanets',    path: '/exoplanets', icon: '🪐', desc: 'Exoplanet explorer' },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.path}>
            <Card
              onClick={() => navigate(item.path)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2, p: 2, cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)' },
                transition: 'all 0.15s',
              }}
            >
              <Typography sx={{ fontSize: 28, mb: 0.5 }}>{item.icon}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{item.title}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ── Section: Achievement Badges ───────────────────────────────────────────
  const BadgesSection = () => (
    <Box>
      <SectionTitle icon={<EmojiEvents />} title="Achievement Badges" subtitle="Earn badges by exploring SpacePulse features" />
      {activity?.achievements ? (
        <Grid container spacing={2}>
          {activity.achievements.map(badge => (
            <Grid item xs={6} sm={4} md={3} key={badge.id}>
              <BadgeCard badge={badge} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}
    </Box>
  );

  // ── Section: App Settings ─────────────────────────────────────────────────
  const AppSettingsSection = () => (
    <Box>
      <SectionTitle icon={<Settings />} title="App Settings" subtitle="Customize your SpacePulse experience" />
      {!editMode && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => setEditMode(true)}
            sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light', width: { xs: '100%', sm: 'auto' } }}>
            Edit Settings
          </Button>
        </Box>
      )}
      {editMode && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} /> : <Save />}
            onClick={handleSave} disabled={saving} sx={{ borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>Save</Button>
          <Button variant="outlined" size="small" startIcon={<Cancel />}
            onClick={() => { setEditMode(false); setForm(profile); }}
            sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.2)', flex: { xs: 1, sm: 'none' } }}>Cancel</Button>
        </Box>
      )}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={!editMode}>
            <InputLabel>Language</InputLabel>
            <Select value={f('language') || 'en'} label="Language"
              onChange={e => setForm(p => ({ ...p, language: e.target.value }))}>
              {[['en','English'],['es','Español'],['fr','Français'],['de','Deutsch'],
                ['ja','日本語'],['zh','中文'],['hi','हिन्दी'],['pt','Português']].map(([v,l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 1.5, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VolumeUp sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>Notification Sounds</Typography>
            </Box>
            <Switch
              checked={fBool('soundNotifications')}
              onChange={e => setForm(p => ({ ...p, soundNotifications: e.target.checked }))}
              disabled={!editMode}
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 1.5, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>Email Notifications</Typography>
            </Box>
            <Switch
              checked={fBool('emailNotifications')}
              onChange={e => setForm(p => ({ ...p, emailNotifications: e.target.checked }))}
              disabled={!editMode}
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Refresh sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Auto-refresh: {f('autoRefreshInterval') || 30}s
              </Typography>
            </Box>
            <Slider
              value={form.autoRefreshInterval || 30}
              onChange={(_, v) => setForm(p => ({ ...p, autoRefreshInterval: v }))}
              min={10} max={120} step={10}
              marks={[{value:10,label:'10s'},{value:60,label:'1m'},{value:120,label:'2m'}]}
              disabled={!editMode}
              sx={{ color: 'primary.main' }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Section: Security ─────────────────────────────────────────────────────
  const SecuritySection = () => (
    <Box>
      <SectionTitle icon={<Security />} title="Security" subtitle="Manage your account security and sessions" />

      <Grid container spacing={2}>
        {/* Change password card */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Lock sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Change Password
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Update your password regularly to keep your account secure.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => setPwDialog(true)}
              sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light' }}>
              Change Password
            </Button>
          </Card>
        </Grid>

        {/* Last login card */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Shield sx={{ color: '#66bb6a' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Account Activity
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Last login: <strong style={{ color: '#e3f2fd' }}>{fmtDateTime(profile?.lastLoginAt)}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Total logins: <strong style={{ color: '#e3f2fd' }}>{profile?.loginCount || 0}</strong>
            </Typography>
          </Card>
        </Grid>

        {/* Logout */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: 'rgba(255,167,38,0.04)', border: '1px solid rgba(255,167,38,0.15)', borderRadius: 2, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Logout sx={{ color: '#ffa726' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Sign Out
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Sign out of SpacePulse on this device.
            </Typography>
            <Button variant="outlined" size="small" startIcon={<Logout />} onClick={logout}
              sx={{ borderRadius: 2, borderColor: 'rgba(255,167,38,0.4)', color: '#ffa726' }}>
              Sign Out
            </Button>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );

  // ── Render active section ────────────────────────────────────────────────
  // Call as functions (not JSX components) to prevent unmount/remount on each render,
  // which would cause form inputs to lose focus on every keystroke.
  const renderSection = () => {
    switch (section) {
      case 'basic':       return BasicInfoSection();
      case 'preferences': return SpacePreferencesSection();
      case 'activity':    return ActivitySection();
      case 'alerts':      return AlertManagementSection();
      case 'watchlist':   return WatchlistSection();
      case 'personal':    return PersonalDashboardSection();
      case 'badges':      return BadgesSection();
      case 'settings':    return AppSettingsSection();
      case 'security':    return SecuritySection();
      default:            return BasicInfoSection();
    }
  };

  // ── Mobile section tabs ──────────────────────────────────────────────────
  const currentSectionLabel = SECTIONS.find(s => s.id === section)?.label || 'Profile';

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.light' }}>
          My Profile
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Manage your account, preferences, and settings
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: 0, md: 3 }, alignItems: 'flex-start' }}>

        {/* ── Sidebar (desktop) ─────────────────────────────────────────── */}
        <Box sx={{
          display: { xs: 'none', md: 'block' },
          width: 220, flexShrink: 0,
          position: 'sticky', top: 16,
          bgcolor: 'rgba(10,22,40,0.6)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
        }}>
          {Sidebar()}
        </Box>

        {/* ── Main content ──────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Mobile: section tab selector */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Button
              fullWidth variant="outlined"
              endIcon={<ExpandMore sx={{ transition: 'transform 0.2s', transform: mobileTabOpen ? 'rotate(180deg)' : 'none' }} />}
              onClick={() => setMobileTabOpen(v => !v)}
              sx={{
                borderRadius: 2, justifyContent: 'space-between',
                borderColor: 'rgba(255,255,255,0.3)', color: 'primary.light',
                textTransform: 'none', fontSize: 14, fontWeight: 600,
              }}
            >
              {currentSectionLabel}
            </Button>
            {mobileTabOpen && (
              <Card sx={{ mt: 0.5, bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden' }}>
                {SECTIONS.map(s => (
                  <Box
                    key={s.id}
                    onClick={() => { setSection(s.id); setMobileTabOpen(false); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2,
                      bgcolor: section === s.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <Box sx={{ color: section === s.id ? 'primary.main' : 'text.secondary', fontSize: 16 }}>{s.icon}</Box>
                    <Typography variant="body2" sx={{ color: section === s.id ? 'primary.light' : 'text.secondary', fontWeight: section === s.id ? 700 : 400 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}
          </Box>

          {/* Section content card */}
          <Card sx={{
            bgcolor: 'rgba(10,22,40,0.6)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: { xs: 2, md: 3 },
            p: { xs: 2, sm: 2.5, md: 3.5 },
          }}>
            {renderSection()}
          </Card>
        </Box>
      </Box>

      {/* ── Change Password Dialog ───────────────────────────────────────── */}
      <Dialog open={pwDialog} onClose={() => setPwDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3 } }}>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>Change Password</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{pwError}</Alert>}
          <TextField fullWidth label="Current Password" type="password" value={pwForm.currentPassword}
            onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))}
            sx={{ mb: 2, mt: 1 }} size="small" />
          <TextField fullWidth label="New Password" type="password" value={pwForm.newPassword}
            onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
            sx={{ mb: 2 }} size="small" helperText="Minimum 6 characters" />
          <TextField fullWidth label="Confirm New Password" type="password" value={pwForm.confirmNew}
            onChange={e => setPwForm(p => ({ ...p, confirmNew: e.target.value }))}
            size="small" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwDialog(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={pwLoading} sx={{ borderRadius: 2 }}>
            {pwLoading ? <CircularProgress size={18} color="inherit" /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
